import React, { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import dayjs from "dayjs"

import { getOrderData, reconcilePayment, cancelOrder } from "../hooks/payments"
import Loading from "../components/Loading"
import PaymentModal from "../components/PaymentModal"

export default function Payment() {
    const { orderNumber } = useParams()
    const [searchParams] = useSearchParams()
    const { t } = useTranslation()

    const [isLoading, setIsLoading] = useState(true)
    const [isClicking, setIsClicking] = useState(false)
    const [orderData, setOrderData] = useState({
        eventId: null,
        qrUrl: null,
        expiredDate: null,
        price: null,
        orderType: ""
    })
    const [modal, setModal] = useState({
        isOpen: false,
        type: 'alert',
        title: '',
        message: '',
        onConfirm: null,
        redirectUrl: null
    })

    const showModal = (type, title, message, onConfirm = null, redirectUrl = null) => {
        setModal({
            isOpen: true,
            type,
            title,
            message,
            onConfirm,
            redirectUrl
        })
    }

    const closeModal = () => {
        const { redirectUrl } = modal
        setModal(prev => ({ ...prev, isOpen: false }))
        
        if (redirectUrl) {
            const lang = searchParams.get("lang")
            if (lang === "en") {
                window.location.href = `${redirectUrl}?lang=en`
            } else {
                window.location.href = redirectUrl
            }
        }
    }

    const fetchOrderData = async () => {
        try {
            const response = await getOrderData(orderNumber)
            const data = response.data
            // console.log(data)
            setOrderData({
                eventId: data.eventId,
                qrUrl: `${process.env.REACT_APP_API_COVID19}/api/uploads/getItemFromS3/${data.QRUrl}`,
                expiredDate: data.expiredDateTime,
                price: data.finalPrices.toString(),
                orderType: data.type
            })
            setIsLoading(false)
        } catch (error) {
            // console.log(error)
            let redirectUrl = "/"

            if (error.status) {
                const status = error.status

                if (status === 301) {
                    showModal('alert', t("alertTitle"), t("errorPaymentAlreadyPaid"), null, "/ticket")
                    return
                } else if (status === 401) {
                    localStorage.clear()
                    showModal('error', t("alertTitle"), t("errorSessionExpired"), null, "/")
                    return
                } else if (status === 404) {
                    showModal('error', t("alertTitle"), t("errorOrderNotFound"), null, "/")
                    return
                } else if (status === 410) {
                    showModal('error', t("alertTitle"), t("errorOrderExpired"), null, "/")
                    return
                } else if (status === 502) {
                    showModal('error', t("alertTitle"), `${t("errorServerError")} [502]`, null, "/")
                    return
                }
            }

            showModal('error', t("alertTitle"), t("errorServerError"), null, redirectUrl)
        }
    }

    const handleCheckPaymentStatus = async () => {
        setIsClicking(true)

        try {
            await reconcilePayment(orderNumber)
            showModal('success', t("alertTitle"), t("paymentCompleteAlert"), null, "/ticket")
        } catch (error) {
            if (error.status) {
                const status = error.status

                if (status === 401) {
                    localStorage.clear()
                    showModal('error', t("alertTitle"), t("errorSessionExpired"), null, "/")
                    return
                } else if (status === 402) {
                    showModal('alert', t("alertTitle"), t("errorPleasePayViaMobile"))
                } else if (status === 404) {
                    showModal('error', t("alertTitle"), t("errorServerError"), null, "/")
                    return
                } else if (status === 502) {
                    showModal('error', t("alertTitle"), `${t("errorCannotCheckPayment")} [502]`)
                } else {
                    showModal('error', t("alertTitle"), t("errorCannotCheckPayment"))
                }
            } else {
                showModal('error', t("alertTitle"), t("errorCannotCheckPayment"))
            }

            setIsClicking(false)
        }
    }

    const handleCancelBooking = () => {
        showModal(
            'confirm',
            t("confirmCancelTitle"),
            t("confirmCancelMessage"),
            confirmCancelBooking
        )
    }

    const confirmCancelBooking = async () => {
        setIsClicking(true)
        setModal(prev => ({ ...prev, isOpen: false }))

        try {
            await cancelOrder(orderNumber)
            const redirectUrl = `/booking/${orderData.eventId}`
            showModal('success', t("alertTitle"), t("cancelSuccessAlert"), null, redirectUrl)
        } catch (error) {
            if (error.status) {
                const status = error.status

                if (status === 301) {
                    showModal('error', t("alertTitle"), t("errorPaymentCompleteCannotCancel"), null, "/ticket")
                    return
                } else if (status === 401) {
                    localStorage.clear()
                    showModal('error', t("alertTitle"), t("errorSessionExpired"), null, "/")
                    return
                } else if (status === 404) {
                    showModal('error', t("alertTitle"), t("errorBookingNotFoundOrCanceled"))
                } else if (status === 500 || status === 502) {
                    showModal('error', t("alertTitle"), `${t("errorCannotCancelBooking")} [${status}]`)
                } else {
                    showModal('error', t("alertTitle"), t("errorCannotCancelBooking"))
                }
            } else {
                showModal('error', t("alertTitle"), t("errorCannotCancelBooking"))
            }

            setIsClicking(false)
        }
    }

    useEffect(() => {
        fetchOrderData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (isLoading) return <Loading />

    return (
        <div className="container">
            <div className="row">
                <div className="col-md-4 col-lg-4 col-xl-4"></div>
                <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4 col-xl-4">
                    <div style={{ paddingTop: "5%" }}>
                        <div style={{ width: "90%", margin: "0 auto" }}>
                            <center>
                                <img
                                    alt="QR Code"
                                    src={orderData.qrUrl || "/images/testSquare.png"}
                                    style={{ width: "100%" }}
                                />
                            </center>
                        </div>
                        <div style={{ marginTop: "5px" }}>
                            <center>
                                <div>{t("paymentExpireHeader")}:</div>
                                <div>
                                    {orderData.expiredDate && orderData.expiredDate.includes("+07:00") && (
                                        <div>
                                            {dayjs(orderData.expiredDate).format("DD/MM/YYYY HH:mm")}
                                        </div>
                                    )}
                                    {orderData.expiredDate && !orderData.expiredDate.includes("+07:00") && (
                                        <div>
                                            {orderData.expiredDate}
                                        </div>
                                    )}
                                </div>
                            </center>
                            <br />
                            <div className="d-grid gap-2">
                                <button
                                    className="btn btn-primary btn-block"
                                    onClick={handleCheckPaymentStatus}
                                    disabled={isClicking}
                                >
                                    {t("checkPaymentStatusBtn")}
                                </button>
                                {orderData.orderType !== "" && (
                                    <button
                                        className="btn btn-outline-danger btn-block"
                                        onClick={handleCancelBooking}
                                        disabled={isClicking}
                                    >
                                        {orderData.orderType === "top-up" && t("cancelTopUp")}
                                        {orderData.orderType === "booking" && t("cancelPaymentBtn")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PaymentModal
                isOpen={modal.isOpen}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onConfirm={modal.onConfirm}
                onClose={closeModal}
            />
        </div>
    )
}
